import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isAfter,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { formatLocalYmd, parseLocalYmd } from '../../lib/date-ymd.ts';

interface Props {
  value: string;
  onChange: (ymd: string) => void;
  label?: string;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Matches Live tab toolbar filter styling. */
export const historyFilterFieldSx = {
  minWidth: 168,
  '& .MuiOutlinedInput-root': {
    height: 40,
    fontSize: '13px',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Roboto, sans-serif',
    '& fieldset': { borderColor: '#E0E0E0' },
    '&:hover fieldset': { borderColor: '#E0E0E0' },
    '&.Mui-focused fieldset': { borderColor: '#2932E5' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '13px',
  },
};

export function FlashbackDatePicker({ value, onChange, label = 'Date' }: Props): ReactElement {
  const [open, setOpen] = useState(false);
  const today = useMemo(() => startOfDay(new Date()), [open]);
  const todayYmd = useMemo(() => formatLocalYmd(today), [today]);
  const selectedDate = useMemo(() => parseLocalYmd(value) ?? today, [value, today]);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selectedDate));

  useEffect(() => {
    if (open) setViewMonth(startOfMonth(selectedDate));
  }, [open, selectedDate]);

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const leadingEmpty = monthStart.getDay();

  const displayValue = useMemo(() => format(selectedDate, 'MM/dd/yyyy'), [selectedDate]);

  const canGoNextMonth = !isAfter(addMonths(viewMonth, 1), today);

  const handleSelect = (day: Date): void => {
    if (isAfter(startOfDay(day), today)) return;
    onChange(formatLocalYmd(day));
    setOpen(false);
  };

  const openCalendar = (): void => setOpen(true);

  return (
    <>
      <TextField
        size="small"
        label={label}
        value={displayValue}
        onClick={openCalendar}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          readOnly: true,
          'aria-label': `${label}: ${displayValue}. Open calendar.`,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon sx={{ color: 'var(--app-text-secondary)', fontSize: 22 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          ...historyFilterFieldSx,
          '& .MuiInputBase-input': { cursor: 'pointer' },
          '& .MuiOutlinedInput-root': {
            ...historyFilterFieldSx['& .MuiOutlinedInput-root'],
            cursor: 'pointer',
          },
        }}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1400 }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
        PaperProps={{
          sx: {
            borderRadius: 'var(--app-radius-container)',
            boxShadow: 'var(--app-shadow-card)',
            overflow: 'visible',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid var(--app-border)',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Select date
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close calendar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              aria-label="Previous month"
              sx={{ border: '1px solid var(--app-border)', borderRadius: '6px' }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {format(viewMonth, 'MMMM yyyy')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              disabled={!canGoNextMonth}
              aria-label="Next month"
              sx={{ border: '1px solid var(--app-border)', borderRadius: '6px' }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
              mb: 0.5,
            }}
          >
            {DAY_LABELS.map((day) => (
              <Typography
                key={day}
                variant="caption"
                sx={{
                  textAlign: 'center',
                  fontWeight: 600,
                  color: 'var(--app-text-secondary)',
                  py: 0.5,
                }}
              >
                {day}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {Array.from({ length: leadingEmpty }, (_, i) => (
              <Box key={`empty-${i}`} sx={{ aspectRatio: '1' }} />
            ))}
            {daysInMonth.map((day) => {
              const isFuture = isAfter(startOfDay(day), today);
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, today);
              const inViewMonth = isSameMonth(day, viewMonth);

              return (
                <Button
                  key={day.toISOString()}
                  type="button"
                  disabled={isFuture || !inViewMonth}
                  onClick={() => handleSelect(day)}
                  sx={{
                    aspectRatio: '1',
                    minWidth: 0,
                    p: 0,
                    fontSize: 13,
                    fontWeight: isSelected ? 700 : 400,
                    borderRadius: '6px',
                    color: isSelected ? '#fff' : 'var(--app-text-primary)',
                    bgcolor: isSelected ? 'var(--app-primary)' : 'transparent',
                    border: isToday && !isSelected ? '2px solid var(--app-primary)' : '1px solid transparent',
                    '&:hover:not(:disabled)': {
                      bgcolor: isSelected ? 'var(--app-primary)' : 'rgba(41, 50, 229, 0.08)',
                    },
                    '&:disabled': {
                      color: 'var(--app-text-secondary)',
                      opacity: 0.35,
                    },
                  }}
                >
                  {format(day, 'd')}
                </Button>
              );
            })}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              pt: 1.5,
              borderTop: '1px solid var(--app-border)',
            }}
          >
            <Button
              type="button"
              size="small"
              onClick={() => {
                onChange(todayYmd);
                setOpen(false);
              }}
              sx={{ textTransform: 'none', fontSize: 13 }}
            >
              Today
            </Button>
            <Typography variant="caption" sx={{ color: 'var(--app-text-secondary)' }}>
              Future dates are disabled
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
